import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AppToast from './AppToast';
import { dmAPI, managementAPI } from '../services/api';

/** Mantiene el composer arriba del teclado virtual (iOS/Android) y actualiza --dm-keyboard */
function useKeyboardInset() {
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--dm-keyboard', `${inset}px`);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      document.documentElement.style.removeProperty('--dm-keyboard');
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
}

function OwnerMassMessages({ selectedCompanyId }) {
  const [factories, setFactories] = useState([]);
  const [factoryId, setFactoryId] = useState('');
  const [eligibleCount, setEligibleCount] = useState(null);
  const [eligibleErr, setEligibleErr] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [lastBroadcast, setLastBroadcast] = useState(null);

  useEffect(() => {
    (async () => {
      setLastBroadcast(null);
      if (!selectedCompanyId) {
        setFactories([]);
        setFactoryId('');
        return;
      }
      try {
        setLoading(true);
        const res = await managementAPI.factories(Number(selectedCompanyId));
        const rows = res.data || [];
        setFactories(rows);
        setFactoryId(rows[0] ? String(rows[0].id) : '');
      } catch {
        setFactories([]);
        setFactoryId('');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!factoryId) {
      setEligibleCount(null);
      setEligibleErr('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setEligibleErr('');
        const res = await dmAPI.broadcastEligible(Number(factoryId), 'factory_employees');
        if (!cancelled) setEligibleCount(res.data?.count ?? 0);
      } catch (e) {
        if (!cancelled) {
          setEligibleCount(0);
          setEligibleErr(e?.response?.data?.detail || 'No se pudo cargar destinatarios');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [factoryId]);

  const send = async () => {
    if (!factoryId || !text.trim()) return;
    const trimmed = text.trim();
    try {
      setErr('');
      setToast('');
      setSending(true);
      const res = await dmAPI.broadcast({
        factory_id: Number(factoryId),
        content: trimmed,
        recipient_scope: 'factory_employees',
      });
      const n = res.data?.sent ?? 0;
      const recipients = res.data?.recipients ?? [];
      const factoryName = factories.find((f) => String(f.id) === factoryId)?.name || 'Sede';
      const messagePreview = trimmed.length > 140 ? `${trimmed.slice(0, 140)}…` : trimmed;
      setLastBroadcast({
        factoryName,
        sentAt: Date.now(),
        messagePreview,
        recipients,
      });
      setText('');
      setToast(`Mensaje enviado a ${n} persona${n === 1 ? '' : 's'}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo enviar el comunicado');
    } finally {
      setSending(false);
    }
  };

  if (!selectedCompanyId) {
    return (
      <div className="px-3 py-6">
        <p className="text-sm text-slate-600">Seleccioná una empresa en el encabezado para enviar mensajes masivos por sede.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-2 py-3">
      <div className="card-mobile">
        <h2 className="text-lg font-semibold text-gray-900">Mensajes masivos</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enviá el mismo texto por mensaje directo a todos los empleados activos de la sede elegida (no incluye otros
          dueños).
        </p>
      </div>

      <div className="card-mobile space-y-3">
        <label className="block">
          <span className="text-sm font-semibold text-slate-800">Sede</span>
          <select
            className="input-mobile mt-1 w-full"
            value={factoryId}
            onChange={(e) => setFactoryId(e.target.value)}
            disabled={loading || sending || factories.length === 0}
          >
            {factories.length === 0 ? (
              <option value="">No hay sedes</option>
            ) : (
              factories.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))
            )}
          </select>
        </label>
        {eligibleErr && <p className="text-sm text-amber-700">{eligibleErr}</p>}
        {!eligibleErr && eligibleCount !== null && (
          <p className="text-sm text-slate-600">
            Destinatarios: <strong>{eligibleCount}</strong> empleado{eligibleCount === 1 ? '' : 's'}
          </p>
        )}
        <label className="block">
          <span className="text-sm font-semibold text-slate-800">Mensaje</span>
          <textarea
            className="input-mobile textarea-message mt-1 w-full min-h-[7rem] resize-y"
            placeholder="Escribí el comunicado…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={4000}
            disabled={sending || !factoryId}
          />
          <span className="text-xs text-slate-500">{text.length}/4000</span>
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <button
          type="button"
          className="btn-mobile btn-primary w-full"
          onClick={send}
          disabled={sending || !factoryId || !text.trim() || eligibleCount === 0 || eligibleCount === null}
        >
          {sending ? 'Enviando…' : 'Enviar a todos'}
        </button>
      </div>

      {lastBroadcast && (lastBroadcast.recipients?.length ?? 0) > 0 && (
        <div className="card-mobile space-y-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Destinatarios del último envío</h3>
            <p className="text-xs text-slate-500 mt-1">
              {lastBroadcast.factoryName} ·{' '}
              {new Date(lastBroadcast.sentAt).toLocaleString('es-AR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </p>
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words border-l-2 border-slate-200 pl-2">
              {lastBroadcast.messagePreview}
            </p>
          </div>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm text-left min-w-[300px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="py-2 pr-3 font-semibold">Empleado</th>
                  <th className="py-2 font-semibold">Email</th>
                </tr>
              </thead>
              <tbody>
                {lastBroadcast.recipients.map((r) => (
                  <tr key={r.user_id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 align-top">{r.name}</td>
                    <td className="py-2 align-top break-all text-slate-700">{r.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AppToast message={toast} variant="success" onClose={() => setToast('')} />
    </div>
  );
}

const DirectMessages = ({ pendingConversationKey = null, onPendingConversationConsumed, selectedCompanyId = '' }) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const messageEndRef = useRef(null);

  useKeyboardInset();

  const selected = useMemo(() => contacts.find((c) => `${c.user_id}:${c.factory_id}` === selectedKey), [contacts, selectedKey]);

  const loadMessages = useCallback(async () => {
    if (!selected) {
      setMessages([]);
      return;
    }
    const res = await dmAPI.messages(selected.user_id, selected.factory_id);
    setMessages(res.data || []);
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await dmAPI.contacts();
        const list = res.data || [];
        if (cancelled) return;
        setContacts(list);
        const pending = pendingConversationKey;
        const keyOf = (c) => `${c.user_id}:${c.factory_id}`;
        if (pending && list.some((c) => keyOf(c) === pending)) {
          setSelectedKey(pending);
        } else if (list.length) {
          if (pending) {
            setErr(
              'Ese contacto no figura en Mensajes para esta sede. Pedí al administrador que verifique la asignación de supervisión o permisos.'
            );
          }
          setSelectedKey(keyOf(list[0]));
        }
        if (pending) onPendingConversationConsumed?.();
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'No se pudieron cargar los contactos de DM');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setErr('');
        await loadMessages();
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudieron cargar los mensajes');
      }
    })();
  }, [loadMessages, selectedKey]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, selectedKey]);

  const send = async () => {
    if (!selected || !text.trim()) return;
    try {
      setErr('');
      await dmAPI.send({
        factory_id: selected.factory_id,
        recipient_user_id: selected.user_id,
        content: text.trim(),
      });
      setText('');
      await loadMessages();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo enviar el mensaje');
    }
  };

  if (user?.role === 'owner') {
    return <OwnerMassMessages selectedCompanyId={selectedCompanyId} />;
  }

  return (
    <div className="dm-screen">
      <div className="dm-chat-stack">
        <header className="dm-header">
          {selected ? (
            <div className="dm-header-avatar" aria-hidden>
              {(selected.name || '?').charAt(0).toUpperCase()}
            </div>
          ) : (
            <div className="dm-header-avatar opacity-50" aria-hidden>
              ?
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="dm-header-label">Conversar con</div>
            <select
              id="dm-contact"
              className="dm-header-select"
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              disabled={loading}
              aria-label="Elegir contacto"
            >
              <option value="">Elegir contacto…</option>
              {contacts.map((c) => (
                <option key={`${c.user_id}:${c.factory_id}`} value={`${c.user_id}:${c.factory_id}`}>
                  {c.name} · {c.factory_name}
                </option>
              ))}
            </select>
          </div>
        </header>

        {err && (
          <div className="flex-shrink-0 bg-red-600 px-3 py-2 text-sm text-white" role="alert">
            {err}
          </div>
        )}

        <div className="dm-messages px-2 py-2">
          {contacts.length === 0 && !loading && (
            <p className="px-2 py-8 text-center text-sm text-slate-600">No hay contactos disponibles para enviar mensajes.</p>
          )}
          {loading && contacts.length === 0 && <p className="py-8 text-center text-sm text-slate-600">Cargando…</p>}
          {selected && messages.length === 0 && !loading && (
            <p className="px-2 py-8 text-center text-sm text-slate-600/90">Aún no hay mensajes. Escribí abajo para empezar.</p>
          )}
          {messages.map((m) => {
            const mine = Number(m.sender_user_id) === Number(user?.id);
            return (
              <div key={m.id} className={`mb-1.5 flex w-full last:mb-0 ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`flex max-w-[min(88%,20rem)] flex-col sm:max-w-[min(85%,24rem)] lg:max-w-md ${
                    mine ? 'items-end' : 'items-start'
                  }`}
                >
                  <div
                    className={`max-w-full rounded-lg px-2 py-1 shadow-sm ${
                      mine
                        ? 'rounded-br-none bg-[#d9fdd3] text-slate-900'
                        : 'rounded-bl-none border border-slate-200/80 bg-white text-slate-900'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words text-[0.9375rem] leading-snug">{m.content}</p>
                    <p
                      className={`mt-0.5 text-right text-[0.65rem] tabular-nums leading-none ${
                        mine ? 'text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      {new Date(m.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messageEndRef} />
        </div>
      </div>

      <div className="dm-composer">
        <div className="dm-composer-inner">
          <input
            className="dm-input"
            type="text"
            inputMode="text"
            enterKeyHint="send"
            autoComplete="off"
            autoCorrect="on"
            placeholder="Mensaje"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={!selected}
          />
          <button type="button" className="dm-send" onClick={send} disabled={!selected || !text.trim()} aria-label="Enviar">
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectMessages;
